import type { VisibilityState } from '@tanstack/react-table';
import { useMemo } from 'react';
import { Actions } from '../../../../components/product-table/columns/actions-column';
import { CaseQty } from '../../../../components/product-table/columns/case-qty-column';
import { CurrentCost } from '../../../../components/product-table/columns/current-cost-column';
import { Department } from '../../../../components/product-table/columns/department-column';
import { DOH } from '../../../../components/product-table/columns/doh-column';
import { FutureCost } from '../../../../components/product-table/columns/future-cost-column';
import { ItemCode } from '../../../../components/product-table/columns/item-code-column';
import { LastCaseCost } from '../../../../components/product-table/columns/last-case-cost-column';
import { LastCost } from '../../../../components/product-table/columns/last-cost-column';
import { LineNumber } from '../../../../components/product-table/columns/line-number-column';
import { MarginDollars } from '../../../../components/product-table/columns/margin-dollars-column';
import { MarginPercentage } from '../../../../components/product-table/columns/margin-percentage-column';
import { OrderLot } from '../../../../components/product-table/columns/order-lot-column';
import { Price } from '../../../../components/product-table/columns/price-column';
import { QOHCases } from '../../../../components/product-table/columns/qoh-cases-column';
import { QOH } from '../../../../components/product-table/columns/qoh-column';
import {
  getCasesSoldColumn,
  getUnitsSoldColumn,
} from '../../../../components/product-table/columns/sales-columns';
import { SavingsDollars } from '../../../../components/product-table/columns/savings-dollars-column';
import {
  DealsSavingsIndicator,
  VolumeSavingsIndicator,
} from '../../../../components/product-table/columns/savings-indicator-column';
import { SavingsPercentage } from '../../../../components/product-table/columns/savings-percentage-column';
import { Size } from '../../../../components/product-table/columns/size-column';
import { StdQty } from '../../../../components/product-table/columns/std-qty-column';
import { Type } from '../../../../components/product-table/columns/type-column';
import type { StrictColumnDef } from '../../../../components/tanstack-table/column';
import { isPivotEligible } from '../../../../data/product';
import { hasSwingDeals } from '../../../../data/states';
import { useVisibleColumns } from '../../../../hooks/use-visible-columns';
import { AnnualProfit } from '../components/annual-profit-column';
import { makeCountColumn } from '../components/count-column';
import { extendedBuyupCostColumn } from '../components/extended-buyup-cost-column';
import { extendedCostColumn } from '../components/extended-cost-column';
import { OpportunityCost } from '../components/opportunity-cost-column';
import { OutOfStockRisk } from '../components/out-of-stock-risk-column';
import { makeProductPivotColumn } from '../components/product-pivot-column';
import { SavingsPotential90d } from '../components/savings-potential-column';
import { swingSavingsColumn } from '../components/swing-savings-column';
import type { ProductOrderItem } from '../data/product-order-item';
import type { ColumnOptions } from '../types';
import { wrapProductColumn } from '../utils';

export const BUYUP_COLUMNS_KEY = 'order-buyup-columns';
export const ORDER_COLUMNS_KEY = 'order-columns';

export const DEFAULT_HIDDEN_COLUMNS = [OpportunityCost.id, AnnualProfit.id, OutOfStockRisk.id];

export const PINNED_COLUMNS = ['Product'];

type ColumnsResult = {
  /** All columns (including hidden) — pass to TanStackTable for sorting */
  columns: StrictColumnDef<ProductOrderItem>[];
  /** Only visible columns — use for layout/sizing outside the table */
  visibleColumns: StrictColumnDef<ProductOrderItem>[];
  /** TanStack visibility state — pass to TanStackTable */
  columnVisibility: VisibilityState;
  /** TanStack columnOrder state */
  columnOrder: string[];
};

function useBuyupColumns({
  store,
  setOrderItem,
  reorderColumn,
  onCasesSoldClick,
  onBrandPivot,
}: ColumnOptions): ColumnsResult {
  const allColumns = useMemo(() => {
    const isSwingDeals = hasSwingDeals(store);

    return [
      LineNumber,
      wrapProductColumn(ItemCode),
      makeProductPivotColumn({
        onPivot: onBrandPivot,
        isEligible: () => true,
        tooltipText: (product) => `Filter to ${product.brand}`,
        affordance: 'hover-only',
      }),
      wrapProductColumn(Size),
      wrapProductColumn(CaseQty),
      wrapProductColumn(CurrentCost),
      isSwingDeals && wrapProductColumn(FutureCost),
      isSwingDeals && wrapProductColumn(SavingsDollars),
      isSwingDeals && wrapProductColumn(SavingsPercentage),
      wrapProductColumn(DOH),
      wrapProductColumn(QOHCases),
      wrapProductColumn(getCasesSoldColumn(30, onCasesSoldClick)),
      wrapProductColumn(getCasesSoldColumn(60, onCasesSoldClick)),
      wrapProductColumn(getCasesSoldColumn(90, onCasesSoldClick)),
      wrapProductColumn(getCasesSoldColumn(180, onCasesSoldClick)),
      wrapProductColumn(getCasesSoldColumn(365, onCasesSoldClick)),
      makeCountColumn(setOrderItem, 'cases'),
      extendedBuyupCostColumn,
      reorderColumn,
      isSwingDeals && swingSavingsColumn,
      SavingsPotential90d,
      isSwingDeals && wrapProductColumn(DealsSavingsIndicator),
      AnnualProfit,
      OutOfStockRisk,
      OpportunityCost,
      wrapProductColumn(Actions),
    ].filter((col): col is StrictColumnDef<ProductOrderItem> => Boolean(col));
  }, [store, setOrderItem, reorderColumn, onCasesSoldClick, onBrandPivot]);

  return useVisibleColumns(allColumns, BUYUP_COLUMNS_KEY, {
    defaultHiddenColumnIds: DEFAULT_HIDDEN_COLUMNS,
  });
}

function useOrderColumns({
  store,
  vendorHasVolume,
  setOrderItem,
  reorderColumn,
  showsSavingsIndicators,
  displayInCases,
  onPivotToBuyup,
}: ColumnOptions): ColumnsResult {
  const allColumns = useMemo(() => {
    const qohColum = displayInCases ? QOHCases : QOH;
    const salesColumn = displayInCases ? getCasesSoldColumn(30) : getUnitsSoldColumn(30);
    const costColumn = displayInCases ? LastCaseCost : LastCost;

    const columns = [
      wrapProductColumn(ItemCode),
      makeProductPivotColumn({
        onPivot: onPivotToBuyup,
        isEligible: (product) => isPivotEligible(product, vendorHasVolume),
        tooltipText: (product) => `Jump to ${product.brand} buyup deals`,
        shortcutHint: 'meta+click',
      }),
      wrapProductColumn(Department),
      wrapProductColumn(Type),
      wrapProductColumn(Size),
      wrapProductColumn(CaseQty),
      wrapProductColumn(StdQty),
      wrapProductColumn(OrderLot),
      wrapProductColumn(DOH),
      wrapProductColumn(qohColum),
      wrapProductColumn(salesColumn),
      makeCountColumn(setOrderItem, 'cases'),
      makeCountColumn(setOrderItem, 'units'),
      wrapProductColumn(costColumn),
      extendedCostColumn,
      reorderColumn,
      wrapProductColumn(Price),
      wrapProductColumn(MarginPercentage),
      wrapProductColumn(MarginDollars),
      AnnualProfit,
      OutOfStockRisk,
      OpportunityCost,
    ];
    if (showsSavingsIndicators && vendorHasVolume) {
      columns.push(wrapProductColumn(VolumeSavingsIndicator));
    }

    if (showsSavingsIndicators && hasSwingDeals(store)) {
      columns.push(wrapProductColumn(DealsSavingsIndicator));
    }

    columns.push(wrapProductColumn(Actions));

    return columns;
  }, [
    setOrderItem,
    reorderColumn,
    showsSavingsIndicators,
    displayInCases,
    store,
    vendorHasVolume,
    onPivotToBuyup,
  ]);

  return useVisibleColumns(allColumns, ORDER_COLUMNS_KEY, {
    defaultHiddenColumnIds: DEFAULT_HIDDEN_COLUMNS,
  });
}

export function useColumns(columnOptions: ColumnOptions): ColumnsResult {
  const buyupResult = useBuyupColumns(columnOptions);
  const orderResult = useOrderColumns(columnOptions);

  if (columnOptions.isBuyup) {
    return buyupResult;
  }

  return orderResult;
}
